import React, { ReactNode, useEffect, useState } from 'react'
import { Image, StyleSheet, Text, View } from 'react-native'
import { Block, BlockComponentProps, ImageFields } from '../../types'
import { layout, space, typography } from '../../tokens'
import { useDocletAssets } from '../../DocletAssetsContext'
import { imageUri } from './imageUri'
import { safeLink } from '../../links'

interface Size {
  width: number
  height: number
}

/**
 * The image's own size: as saved on the block, or -- for images saved without
 * one, such as migrated URLs -- measured once it loads. `failed` when it
 * can't be measured (the image doesn't load).
 */
const useImageSize = (
  uri: string | null,
  width: number | null,
  height: number | null
): { size: Size | null; failed: boolean } => {
  const [measured, setMeasured] = useState<
    (((Size & { failed?: false }) | { failed: true }) & { uri: string }) | null
  >(null)
  const known = !!(width && height)

  useEffect(() => {
    if (!uri || known) return
    let active = true
    Image.getSize(
      uri,
      (w, h) => active && setMeasured({ uri, width: w, height: h }),
      () => active && setMeasured({ uri, failed: true })
    )
    return () => {
      active = false
    }
  }, [uri, known])

  if (known) {
    return {
      size: { width: width as number, height: height as number },
      failed: false
    }
  }
  if (!measured || measured.uri !== uri) return { size: null, failed: false }
  return measured.failed
    ? { size: null, failed: true }
    : {
        size: { width: measured.width, height: measured.height },
        failed: false
      }
}

// A block holding a picture: the Image block, or Image + text
export interface ImageHolder {
  props: ImageFields
  fullWidth?: boolean
}

export interface DocletImageState {
  // null when there's no image, or uploads can't be shown (no Cloudinary cloud)
  uri: string | null
  size: Size | null
  // It won't load (a migrated URL that 404s, a deleted upload)
  failed: boolean
  onLoadError: () => void
}

/**
 * Where the block's image comes from and whether it loads. The block decides
 * from this whether to render at all, and DocletImage draws from it, so the
 * two never disagree.
 */
export const useDocletImage = (block: ImageHolder): DocletImageState => {
  const { cloudinaryCloudName } = useDocletAssets()
  const { source, width, height } = block.props
  const uri = source ? imageUri(source, cloudinaryCloudName) : null
  const { size, failed: measureFailed } = useImageSize(uri, width, height)
  // The image itself failing to load, even when its size was already known
  const [loadFailedFor, setLoadFailedFor] = useState<string | null>(null)
  return {
    uri,
    size,
    failed: !!uri && (measureFailed || loadFailedFor === uri),
    onLoadError: () => uri && setLoadFailedFor(uri)
  }
}

/**
 * Just the picture, at the width it takes on the page: the text column (or
 * the page's edges when full width), never wider than the image itself unless
 * full width. Also used by the builder's editor, which passes a `fallback` to
 * show when the image won't load; the page shows nothing (and ImageBlock
 * leaves out the whole block, caption included).
 */
export const DocletImage = ({
  block,
  image,
  fallback = null
}: {
  block: ImageHolder
  image: DocletImageState
  fallback?: ReactNode
}) => {
  const { uri, size, failed, onLoadError } = image
  if (!uri) return null
  if (failed) return <>{fallback}</>

  return (
    <View
      style={{
        width: '100%',
        maxWidth: block.fullWidth ? undefined : size?.width
      }}
    >
      {/* The box gets its height from percentage padding (relative to its
          width) and the image fills it. Not the aspectRatio style:
          react-native-web 0.15 writes it as "3px", which browsers ignore,
          leaving the image zero height. Zero height until the size is known. */}
      <View
        style={{
          width: '100%',
          paddingBottom: size ? `${(size.height / size.width) * 100}%` : 0
        }}
      >
        <Image
          source={{ uri }}
          accessible={!!block.props.alt}
          accessibilityLabel={block.props.alt || undefined}
          resizeMode="cover"
          onError={onLoadError}
          style={StyleSheet.absoluteFill}
        />
      </View>
    </View>
  )
}

// Full-width images run to the page's edges; their caption stays in line with
// the text
export const captionStyle = (block: Block<'image'>) => ({
  ...typography.caption,
  ...(block.fullWidth ? { paddingHorizontal: layout.blockPadding } : {})
})

export const imageBlockStyle = { marginBottom: space[4] }

const ImageLink = ({
  link,
  children
}: {
  link: string
  children: ReactNode
}) => {
  const href = safeLink(link)
  return href ? (
    <View
      accessibilityRole="link"
      // react-native-web renders this as <a href target=_blank>
      {...({
        href,
        hrefAttrs: { target: '_blank', rel: 'noopener noreferrer' }
      } as any)}
    >
      {children}
    </View>
  ) : (
    <>{children}</>
  )
}

// Legacy equivalent: `image`. A block with no image yet is left out of the
// rendered page.
const ImageBlock = ({ block }: BlockComponentProps<'image'>) => {
  const { caption, link } = block.props
  const image = useDocletImage(block)
  // No image, or one that won't load: leave out the whole block, caption too
  if (!image.uri || image.failed) return null

  return (
    <View style={imageBlockStyle}>
      <ImageLink link={link}>
        <DocletImage block={block} image={image} />
      </ImageLink>
      {!!caption.trim() && <Text style={captionStyle(block)}>{caption}</Text>}
    </View>
  )
}

export default ImageBlock
